import { useImpersonation } from "@/contexts/ImpersonationContext";
import { ApolloProvider } from "@apollo/client";
import { setContext } from "@apollo/client/link/context";
import { useLogto } from "@logto/react";
import { useContext, useMemo } from "react";
import { getApolloClient } from "../apollo";
import { UserContext, type UserRole } from "./UserContext";

interface ImpersonationHeaders {
  "Organization-Override"?: string;
  "Region-Override"?: string[];
  "Location-Override"?: string[];
}

const getImpersonationHeaders = ({
  organization,
  region,
  location,
}: {
  organization: string | null;
  region: string | null;
  location: string | null;
}): ImpersonationHeaders => {
  const headers: ImpersonationHeaders = {};

  if (organization) headers["Organization-Override"] = organization;
  if (region) headers["Region-Override"] = [region];
  if (location) headers["Location-Override"] = [location];

  return headers;
};

export const USER_ROLE_PRIORITY: UserRole[] = [
  "everbay_admin",
  "employee",
  "partner",
  "customer",
];

export function getExpectedRole(availableRoles: UserRole[]): UserRole {
  return (
    availableRoles
      .filter((role) => USER_ROLE_PRIORITY.includes(role))
      .sort(
        (a, b) => USER_ROLE_PRIORITY.indexOf(a) - USER_ROLE_PRIORITY.indexOf(b),
      )[0] ?? "customer"
  );
}

export function LogtoApolloProvider({
  children,
}: {
  readonly children: React.ReactNode;
}) {
  const { getAccessToken, signOut } = useLogto();
  const impersonation = useImpersonation();
  const { user } = useContext(UserContext);

  const client = useMemo(() => {
    const authLink = setContext(async (_, { headers }) => ({
      headers: {
        ...headers,
        ...(await getHeaders()),
      },
    }));

    return getApolloClient(authLink, getHeaders);
  }, [getAccessToken, impersonation, user]);

  async function getHeaders(): Promise<Record<string, string>> {
    const [token, impersonationHeaders] = await Promise.all([
      getAccessToken("https://api.gowoop.de"),
      Promise.resolve(getImpersonationHeaders(impersonation)),
    ]);

    if (token === null || token === undefined || token.trim() === "") {
      await signOut();
      return {};
    }

    const expectedRole = user ? getExpectedRole(user.roles) : "customer";
    return {
      "Expected-Role": expectedRole,
      Authorization: `Bearer ${token ?? ""}`,
      ...Object.fromEntries(
        Object.entries(impersonationHeaders).filter(
          ([_, value]) => value !== undefined,
        ),
      ),
    };
  }

  return <ApolloProvider client={client}>{children}</ApolloProvider>;
}
